import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Services from "./pages/Services";
import Location from "./pages/Location";
import Contact from "./pages/Contact";
import { Toaster } from "./components/ui/toaster";

// Auth
import { AuthProvider } from "./context/AuthContext";
import Login from "./pages/Login";
import ProtectedRoute from "./components/ProtectedRoute";

// Operations pages
import OperationsLayout from "./pages/operations/OperationsLayout";
import Dashboard from "./pages/operations/Dashboard";
import Customers from "./pages/operations/Customers";
import Items from "./pages/operations/Items";
import DeliveryNotes from "./pages/operations/DeliveryNotes";
import NewDeliveryNote from "./pages/operations/NewDeliveryNote";
import ViewDeliveryNote from "./pages/operations/ViewDeliveryNote";
import StaffManagement from "./pages/operations/StaffManagement";
import ChangePassword from "./pages/operations/ChangePassword";
import CustomerItems from "./pages/operations/CustomerItems";
import Invoicing from "./pages/operations/Invoicing";
import ViewInvoice from "./pages/operations/ViewInvoice";

// Layout wrapper to conditionally show Navbar/Footer
const Layout = ({ children }) => {
  const location = useLocation();
  const isOperations = location.pathname.startsWith('/operations');
  const isLogin = location.pathname === '/login';

  return (
    <>
      {!isOperations && !isLogin && <Navbar />}
      {children}
      {!isOperations && !isLogin && <Footer />}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Layout>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/location" element={<Location />} />
              <Route path="/contact" element={<Contact />} />
              
              {/* Auth routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/operations/login" element={<Login />} />
              
              {/* Protected Operations routes */}
              <Route 
                path="/operations" 
                element={
                  <ProtectedRoute>
                    <OperationsLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route 
                  path="customers" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <Customers />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="items" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <Items />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="customer-items" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <CustomerItems />
                    </ProtectedRoute>
                  } 
                />
                {/* Step 6 redesign: split delivery notes into Today + Historical sub-pages */}
                <Route path="delivery-notes" element={<Navigate to="/operations/delivery-notes/today" replace />} />
                <Route path="delivery-notes/today" element={<DeliveryNotes mode="today" />} />
                <Route path="delivery-notes/historical" element={<DeliveryNotes mode="historical" />} />
                <Route path="delivery-notes/new" element={<NewDeliveryNote />} />
                <Route path="delivery-notes/:id" element={<ViewDeliveryNote />} />
                <Route 
                  path="staff" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <StaffManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="invoicing" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <Invoicing />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="invoicing/:id" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <ViewInvoice />
                    </ProtectedRoute>
                  } 
                />
                <Route path="change-password" element={<ChangePassword />} />
              </Route>
            </Routes>
          </Layout>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
